/**
 * D&D 5e ATLA — bender progression & lineage chassis (Game Data).
 *
 * Verbatim from "ATLA 5e Homebrew":
 *   · benders' level table .......... p.27
 *   · lineage hit dice / saves ...... pp.11–14
 *   · bending ability & basic attack  Water p.28 · Earth p.31 · Fire p.34 · Air p.36
 *
 * Read-only reference data. Nothing here is derived, smoothed or invented —
 * where the source is silent (e.g. Air Nomads' optional Dexterity swap) the
 * caveat is carried as text, not resolved.
 */
import type { SourceRef } from '@/domain/shared';

export const GM_BINDER = 'ATLA 5e Homebrew';

export interface BenderLevelRow {
  level: number;
  proficiency: number;
  features: string;
  knownTechniques: number;
  practiced: number;
  trained: number;
  mastered: number;
}

/** ATLA 5e Homebrew p.27 — "Benders" table, transcribed row for row. */
export const BENDER_TABLE: readonly BenderLevelRow[] = [
  { level: 1, proficiency: 2, features: 'Bending Feature', knownTechniques: 2, practiced: 3, trained: 0, mastered: 0 },
  { level: 2, proficiency: 2, features: '—', knownTechniques: 3, practiced: 3, trained: 0, mastered: 0 },
  { level: 3, proficiency: 2, features: 'Bending Teaching', knownTechniques: 4, practiced: 4, trained: 1, mastered: 0 },
  { level: 4, proficiency: 2, features: 'Ability Score Improvement', knownTechniques: 5, practiced: 4, trained: 1, mastered: 1 },
  { level: 5, proficiency: 3, features: 'Bending Feature', knownTechniques: 6, practiced: 4, trained: 2, mastered: 1 },
  { level: 6, proficiency: 3, features: '—', knownTechniques: 7, practiced: 5, trained: 2, mastered: 1 },
  { level: 7, proficiency: 3, features: 'Teaching Improvement', knownTechniques: 8, practiced: 5, trained: 2, mastered: 2 },
  { level: 8, proficiency: 3, features: 'Bending Feature', knownTechniques: 9, practiced: 5, trained: 3, mastered: 2 },
  { level: 9, proficiency: 4, features: 'Ability Score Improvement', knownTechniques: 10, practiced: 6, trained: 3, mastered: 2 },
  { level: 10, proficiency: 4, features: 'Teaching Improvement', knownTechniques: 10, practiced: 6, trained: 4, mastered: 2 },
  { level: 11, proficiency: 4, features: 'Bending Feature', knownTechniques: 11, practiced: 7, trained: 4, mastered: 3 },
  { level: 12, proficiency: 4, features: 'Ability Score Improvement', knownTechniques: 11, practiced: 7, trained: 4, mastered: 3 },
  { level: 13, proficiency: 5, features: 'Teaching Improvement', knownTechniques: 12, practiced: 8, trained: 5, mastered: 3 },
  { level: 14, proficiency: 5, features: 'Bending Feature', knownTechniques: 12, practiced: 8, trained: 5, mastered: 4 },
  { level: 15, proficiency: 5, features: 'Teaching Improvement', knownTechniques: 13, practiced: 8, trained: 5, mastered: 4 },
  { level: 16, proficiency: 5, features: 'Ability Score Improvement, Bending Feature', knownTechniques: 13, practiced: 9, trained: 6, mastered: 4 },
  { level: 17, proficiency: 6, features: '—', knownTechniques: 14, practiced: 9, trained: 6, mastered: 5 },
  { level: 18, proficiency: 6, features: 'Teaching Improvement', knownTechniques: 14, practiced: 10, trained: 7, mastered: 5 },
  { level: 19, proficiency: 6, features: 'Ability Score Improvement, Bending Feature', knownTechniques: 15, practiced: 11, trained: 7, mastered: 5 },
  { level: 20, proficiency: 6, features: '—', knownTechniques: 15, practiced: 12, trained: 8, mastered: 6 },
] as const;

export const BENDER_TABLE_SOURCE: SourceRef = {
  document: GM_BINDER,
  section: 'Benders — level table',
  page: 27,
};

export function benderRow(level: number): BenderLevelRow {
  const clamped = Math.min(20, Math.max(1, Math.round(level)));
  return BENDER_TABLE[clamped - 1]!;
}

// ------------------------------------------------------------------ lineage

export type AbilityKey = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';
/**
 * The four nation lineages (ATLA 5e Homebrew pp.11–14) plus four fanmade sub-lineages
 * whose mechanics are inherited verbatim from their parent nation package —
 * their origins are the sub-origins the source itself prints (p.11 Foggy Swamp,
 * p.12 Si Wong / Kyoshi, p.13 Fire Nation colony) promoted to selectable rows.
 */
export type LineageId =
  | 'water-tribes'
  | 'earth-kingdom'
  | 'fire-nation'
  | 'air-nomads'
  | 'foggy-swamp'
  | 'si-wong-sandbenders'
  | 'kyoshi-island'
  | 'fire-colony';

export interface LineageChassis {
  id: LineageId;
  name: { tr: string; en: string };
  discipline: 'Waterbending' | 'Earthbending' | 'Firebending' | 'Airbending';
  /** Hit die size — 8, 10 or 12 (pp.11–14). */
  hitDie: 6 | 8 | 10 | 12;
  /** "Saving Throws: X and 1 of your choosing." */
  savingThrow: AbilityKey;
  /** "your Bending Ability is …" — drives DC, attack, basic-attack damage. */
  bendingAbility: AbilityKey;
  /** Verbatim basic-attack line from the class page. */
  basicAttack: string;
  /** Jing tendency described on p.4 — flavour, explicitly not a constraint. */
  jing: 'positive' | 'neutral' | 'negative' | 'shifting';
  /** Sub-bending paths as printed in the Table of Contents (pp.29–36). */
  paths: string[];
  /** Any source caveat that must not be silently resolved. */
  note: { tr: string; en: string } | null;
  source: SourceRef;
  /** Fanmade sub-lineage only: the root nation whose package it inherits. */
  parent?: LineageId;
}

export const LINEAGES: Readonly<Record<LineageId, LineageChassis>> = {
  'water-tribes': {
    id: 'water-tribes',
    name: { tr: 'Su Kabileleri', en: 'Water Tribes' },
    discipline: 'Waterbending',
    hitDie: 10,
    savingThrow: 'cha',
    bendingAbility: 'cha',
    basicAttack: '1d8 + Charisma modifier',
    jing: 'shifting',
    paths: ['Path of the Waterbender', 'Path of the Healer', 'Path of the Bloodbender'],
    note: null,
    source: { document: GM_BINDER, section: 'Water Tribes / Waterbending', page: 11 },
  },
  'earth-kingdom': {
    id: 'earth-kingdom',
    name: { tr: 'Toprak Krallığı', en: 'Earth Kingdom' },
    discipline: 'Earthbending',
    hitDie: 12,
    savingThrow: 'con',
    bendingAbility: 'con',
    basicAttack: '1d8 + Constitution modifier (bludgeoning)',
    jing: 'neutral',
    paths: ['Tradition of Earthbending', 'Tradition of Metalbending', 'Tradition of Lavabending'],
    note: {
      tr: 'Metalbending, Aang öncesi çağlarda henüz yoktur (ATLA 5e Homebrew s.8).',
      en: 'Metalbending does not exist in eras before Aang (ATLA 5e Homebrew p.8).',
    },
    source: { document: GM_BINDER, section: 'Earth Kingdom / Earthbending', page: 12 },
  },
  'fire-nation': {
    id: 'fire-nation',
    name: { tr: 'Ateş Ulusu', en: 'Fire Nation' },
    discipline: 'Firebending',
    hitDie: 8,
    savingThrow: 'str',
    bendingAbility: 'str',
    basicAttack: '1d10 + Strength modifier (fire; crit inflames the target)',
    jing: 'positive',
    paths: ['Principles of the Firebender', 'Principles of Combustionbending'],
    note: null,
    source: { document: GM_BINDER, section: 'Fire Nation / Firebending', page: 13 },
  },
  'air-nomads': {
    id: 'air-nomads',
    name: { tr: 'Hava Göçebeleri', en: 'Air Nomads' },
    discipline: 'Airbending',
    hitDie: 6,
    savingThrow: 'wis',
    bendingAbility: 'wis',
    basicAttack: '1d6 + Wisdom modifier (thunder)',
    jing: 'negative',
    paths: ['Airbending'],
    note: {
      tr: 'Kaynak, GM izniyle Bilgelik yerine Çeviklik kullanılabileceğini söylüyor (s.36) — seçim masaya bırakıldı.',
      en: 'The source permits Dexterity instead of Wisdom with GM approval (p.36) — left to the table.',
    },
    source: { document: GM_BINDER, section: 'Air Nomads / Airbending', page: 14 },
  },
  // ------------------------------------------- fanmade sub-lineages (mechanics
  // inherited verbatim from the parent nation row above; only the origin and
  // the flavour note are new. Sources for the origin names: ATLA 5e Homebrew sub-origin
  // lists (pp.11–13) + Player Manual world lore.
  'foggy-swamp': {
    id: 'foggy-swamp',
    name: { tr: 'Batak Bataklığı Kabilesi', en: 'Foggy Swamp Tribe' },
    discipline: 'Waterbending',
    hitDie: 10,
    savingThrow: 'cha',
    bendingAbility: 'cha',
    basicAttack: '1d8 + Charisma modifier',
    jing: 'shifting',
    paths: ['Path of the Waterbender', 'Path of the Healer', 'Path of the Bloodbender'],
    parent: 'water-tribes',
    note: {
      tr: 'Fanmade alt-köken: tüm sayısal paket Su Kabileleri’nden miras (ATLA 5e Homebrew s.11); köken adı hem s.11’in “Foggy Swamp” satırı hem Player Manual’deki Batak Suyu kabilesidir.',
      en: 'Fanmade sub-lineage: the whole numeric package is inherited from the Water Tribes (ATLA 5e Homebrew p.11); the origin comes from the p.11 “Foggy Swamp” line and the Player Manual Foggy Swamp Tribe.',
    },
    source: { document: GM_BINDER, section: 'Water Tribes / Foggy Swamp (sub-origin)', page: 11 },
  },
  'si-wong-sandbenders': {
    id: 'si-wong-sandbenders',
    name: { tr: 'Kum Bükücüler (Si Wong)', en: 'Sandbenders (Si Wong)' },
    discipline: 'Earthbending',
    hitDie: 12,
    savingThrow: 'con',
    bendingAbility: 'con',
    basicAttack: '1d8 + Constitution modifier (bludgeoning)',
    jing: 'neutral',
    paths: ['Tradition of Earthbending', 'Tradition of Metalbending', 'Tradition of Lavabending'],
    parent: 'earth-kingdom',
    note: {
      tr: 'Fanmade alt-köken: Toprak Krallığı paketinden miras (ATLA 5e Homebrew s.12); “Si Wong Desert (Sandbender Tribe)” satırı kaynaktan, bataklık/kum üslubu Player Manual’den.',
      en: 'Fanmade sub-lineage: inherited from the Earth Kingdom package (ATLA 5e Homebrew p.12); the “Si Wong Desert (Sandbender Tribe)” line is from the source, the flavour from the Player Manual.',
    },
    source: { document: GM_BINDER, section: 'Earth Kingdom / Si Wong (sub-origin)', page: 12 },
  },
  'kyoshi-island': {
    id: 'kyoshi-island',
    name: { tr: 'Kyoshi Adası', en: 'Kyoshi Island' },
    discipline: 'Earthbending',
    hitDie: 12,
    savingThrow: 'con',
    bendingAbility: 'con',
    basicAttack: '1d8 + Constitution modifier (bludgeoning)',
    jing: 'neutral',
    paths: ['Tradition of Earthbending', 'Tradition of Metalbending', 'Tradition of Lavabending'],
    parent: 'earth-kingdom',
    note: {
      tr: 'Fanmade alt-köken: Toprak Krallığı paketinden miras (ATLA 5e Homebrew s.12); “Kyoshi Island” satırı kaynaktan, ada kültürü Player Manual’den (Kiyoshi Savaşçıları, Su Kabileleri ticareti).',
      en: 'Fanmade sub-lineage: inherited from the Earth Kingdom package (ATLA 5e Homebrew p.12); the “Kyoshi Island” line is from the source, the culture from the Player Manual (Kyoshi Warriors, Water Tribe trade).',
    },
    source: { document: GM_BINDER, section: 'Earth Kingdom / Kyoshi Island (sub-origin)', page: 12 },
  },
  'fire-colony': {
    id: 'fire-colony',
    name: { tr: 'Ateş Kolonisi', en: 'Fire Nation Colony' },
    discipline: 'Firebending',
    hitDie: 8,
    savingThrow: 'str',
    bendingAbility: 'str',
    basicAttack: '1d10 + Strength modifier (fire; crit inflames the target)',
    jing: 'positive',
    paths: ['Principles of the Firebender', 'Principles of Combustionbending'],
    parent: 'fire-nation',
    note: {
      tr: 'Fanmade alt-köken: Ateş Ulusu paketinden miras (ATLA 5e Homebrew s.13); “Fire Nation colony” satırı kaynaktan, koloni/Cumhuriyet karma kültürü Player Manual’den (Yu Dao, Cranefish Town).',
      en: 'Fanmade sub-lineage: inherited from the Fire Nation package (ATLA 5e Homebrew p.13); the “Fire Nation colony” line is from the source, the colony/Republic melting-pot flavour from the Player Manual (Yu Dao, Cranefish Town).',
    },
    source: { document: GM_BINDER, section: 'Fire Nation / Colony (sub-origin)', page: 13 },
  },
} as const;

/** Older saves stored the English display name instead of the content id. */
const LEGACY_LINEAGE_NAMES: Record<string, LineageId> = {
  'Water Tribes': 'water-tribes',
  'Earth Kingdom': 'earth-kingdom',
  'Fire Nation': 'fire-nation',
  'Air Nomads': 'air-nomads',
};

/** Any stored lineage string (id, legacy name, or null) → a live LineageId. */
export function normalizeLineageId(value: string | null | undefined): LineageId | null {
  if (!value) return null;
  if (value in LINEAGES) return value as LineageId;
  return LEGACY_LINEAGE_NAMES[value] ?? null;
}

/** The root nation for a lineage (sub-lineages inherit the root's mechanics). */
export function rootLineageId(value: string | null | undefined): LineageId | null {
  const id = normalizeLineageId(value);
  if (!id) return null;
  const entry = LINEAGES[id];
  return entry.parent ?? id;
}

/** Non-bending fighting styles (pp.16–26). */
export const NON_BENDER_CLASSES = [
  { id: 'weaponsmaster', name: { tr: 'Silah Ustası', en: 'Weaponsmaster' }, keyAbility: 'str' as AbilityKey, hitDie: 10 as const, page: 16 },
  { id: 'tech-engineer', name: { tr: 'Teknik Mühendis', en: 'Tech-Engineer' }, keyAbility: 'int' as AbilityKey, hitDie: 8 as const, page: 19 },
] as const;

export function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

/** Average HP for a monster-style statblock: level 1 max + (level-1) × avg. */
export function averageHp(hitDie: number, level: number, conMod: number): number {
  const avgPerLevel = hitDie / 2 + 1;
  const total = hitDie + conMod + (level - 1) * (avgPerLevel + conMod);
  return Math.max(1, Math.round(total));
}
